import type { ApplicationJourneyStepDto } from '../api/admin';
import { employmentLastActionOperatorCopy } from './employment/employmentForwardCopy';

/** Canonical high-level journey order for admin "where is the customer?" UI. */
export const MILESTONE_DISPLAY_ORDER: string[] = [
  'LOAN_AMOUNT_SELECTED',
  'LOAN_INTENT_CAPTURED',
  'PHONE_VERIFIED',
  'IDENTITY_VERIFIED',
  'IDENTITY_DEDUPED',
  'IDENTITY_ENRICHED',
  'BANK_CONNECTED',
  'INCOME_VERIFIED',
  'LOAN_CAPACITY_CONFIRMED',
  'EMPLOYMENT_PENDING_REVIEW',
  'EMPLOYMENT_VERIFIED',
  'PERSONAL_DATA_CONFIRMED',
  'DECISIONED',
  'AGREEMENT_VIEWED',
  'AGREEMENT_SIGNED',
  'FUNDED',
];

const MILESTONE_LABELS: Record<string, string> = {
  LOAN_AMOUNT_SELECTED: 'Loan amount chosen',
  LOAN_INTENT_CAPTURED: 'Loan details saved',
  PHONE_VERIFIED: 'Phone number verified',
  IDENTITY_VERIFIED: 'Identity check passed',
  IDENTITY_DEDUPED: 'Duplicate identity check finished',
  IDENTITY_ENRICHED: 'Extra identity data added',
  BANK_CONNECTED: 'Bank account linked',
  INCOME_VERIFIED: 'Income verified',
  LOAN_CAPACITY_CONFIRMED: 'Affordability confirmed',
  EMPLOYMENT_PENDING_REVIEW: 'Employment under review (customer advanced)',
  EMPLOYMENT_VERIFIED: 'Employment verified',
  PERSONAL_DATA_CONFIRMED: 'Personal details confirmed',
  DECISIONED: 'Lending decision recorded',
  AGREEMENT_VIEWED: 'Agreement screen opened',
  AGREEMENT_SIGNED: 'Agreement signed',
  FUNDED: 'Loan funded',
};

export function milestonePlainLabel(code: string): string {
  return MILESTONE_LABELS[code] ?? code.replace(/_/g, ' ').toLowerCase();
}

const DECLINE_LABELS: Record<string, string> = {
  DUPLICATE_IDENTITY: 'Stopped: we already have an application for this identity.',
  IDENTITY_FAILED: 'Stopped: identity verification did not pass.',
  IDENTITY_RISK_BLOCK: 'Stopped: identity did not meet risk rules.',
  INCOME_INSUFFICIENT: 'Stopped: income is below what this product allows.',
  DE_DECLINED: 'Stopped: credit decision engine declined this application.',
  DE_DOCUMENT_NUMBER_MISSING:
    'Stopped: government ID document number was not available for the credit decision.',
  DE_IDENTIFIER_MISSING:
    'Stopped: government ID identifier was not available for the credit decision.',
  DE_EVALUATION_FAILED:
    'Stopped: credit decision engine could not complete evaluation.',
  EMPLOYMENT_MISMATCH: 'Stopped: employment details could not be confirmed.',
  UNDERWRITING_DECLINE: 'Stopped: underwriting / Decision Engine declined this application.',
  POLICY_VIOLATION: 'Stopped: policy rules were not met.',
};

const PROVENIR_CODE_RE = /^\s*([A-Za-z]{1,8}\d{1,4})\s*(?::|$)/;

/** Ops-facing decline copy — keep vendor CODE + prose when Provenir/DE declined. */
export function declineReasonPlain(code: string | null | undefined): string | null {
  if (!code?.trim()) return null;
  const raw = code.trim();
  if (DECLINE_LABELS[raw]) return DECLINE_LABELS[raw]!;

  const match = PROVENIR_CODE_RE.exec(raw);
  if (match) {
    const vendorCode = match[1]!.toUpperCase();
    const colon = raw.indexOf(':');
    const prose =
      colon >= 0 ? raw.slice(colon + 1).replace(/\s+/g, ' ').trim() : '';
    if (prose) {
      return `Decision Engine declined (${vendorCode}): ${prose}`;
    }
    return `Decision Engine declined (${vendorCode}).`;
  }

  return `Declined (${raw.replace(/_/g, ' ')}).`;
}

export function loanStatusPlain(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'In progress — the customer can still complete steps.';
    case 'MANUAL_REVIEW':
      return 'Manual review — awaiting an employee decision before the loan can proceed.';
    case 'APPROVED':
      return 'Approved — waiting for agreement / funding steps.';
    case 'FUNDED':
      return 'Funded — loan has been paid out.';
    case 'DECLINED':
      return 'Declined — this application will not continue.';
    case 'WITHDRAWN':
      return 'Withdrawn — the customer or system cancelled this application.';
    default:
      return status;
  }
}

export function doctrineTransportPlain(
  status: 'STARTED' | 'COMPLETED' | 'FAILED' | 'CANCELLED',
): string {
  switch (status) {
    case 'STARTED':
      return 'Still running';
    case 'COMPLETED':
      return 'Finished running';
    case 'FAILED':
      return 'System error while running';
    case 'CANCELLED':
      return 'Cancelled before completion';
    default:
      return status;
  }
}

export function doctrineOutcomePlain(
  outcome: 'PASS' | 'REVIEW' | 'FAIL' | 'CHALLENGE' | null | undefined,
): string {
  if (outcome == null) return 'No final decision recorded yet.';
  switch (outcome) {
    case 'PASS':
      return 'Passed automated checks.';
    case 'REVIEW':
      return 'Needs a human review.';
    case 'FAIL':
      return 'Did not pass automated checks.';
    case 'CHALLENGE':
      return 'Extra checks or documents may be required.';
    default:
      return String(outcome);
  }
}

export function stepStatusPlain(status: ApplicationJourneyStepDto['status']): string {
  switch (status) {
    case 'OK':
      return 'Completed successfully';
    case 'FAILED':
      return 'Failed — see error message';
    case 'TIMEOUT':
      return 'Timed out — service took too long';
    case 'CIRCUIT_OPEN':
      return 'Temporarily blocked — circuit breaker open';
    case 'SKIPPED':
      return 'Skipped — not required for this run';
    default:
      return status;
  }
}

export function lastActionOutcomePlain(
  o: 'SUCCESS' | 'FAILURE' | 'PENDING' | null | undefined,
): string {
  if (o == null) return 'No recent action outcome on file.';
  switch (o) {
    case 'SUCCESS':
      return 'Last step finished successfully.';
    case 'FAILURE':
      return 'Last step reported a problem (see details below).';
    case 'PENDING':
      return 'Last step is still in progress.';
    default:
      return String(o);
  }
}

export function genericVerificationStatusPlain(status: unknown): string {
  if (status == null || typeof status !== 'string') return '—';
  const u = status.toUpperCase();
  switch (u) {
    case 'SUCCESS':
    case 'COMPLETED':
    case 'VERIFIED':
    case 'PASSED':
      return 'Completed successfully';
    case 'FAILED':
    case 'REJECTED':
      return 'Did not pass — needs attention';
    case 'PENDING':
    case 'IN_PROGRESS':
    case 'PROCESSING':
      return 'Still processing';
    case 'PARTIAL':
      return 'Partially complete';
    default:
      return status.replace(/_/g, ' ');
  }
}

/** Soften income failReasons / lastAction dumps for Overview and Income tab. */
export function plainIncomeFailReason(raw: string, opts?: { pointToIncomeTab?: boolean }): string {
  const tip = opts?.pointToIncomeTab ? ' See the Income tab for details.' : '';
  if (/not latest|stream is not latest|exceeds\s+\d+d\s+max|IRREGULAR/i.test(raw)) {
    return `Recent pay deposits look too old for the expected pay pattern.${tip}`.trim();
  }
  if (
    /INSUFFICIENT|not enough/i.test(raw) ||
    (/income verification (fail|failed)/i.test(raw) && /\(\$[\d.]+\/mo\)/i.test(raw))
  ) {
    return `Verified income was not enough for this product.${tip}`.trim();
  }
  const cleaned = raw.replace(/^\[[^\]]+\]\s*/i, '').trim();
  if (!cleaned) return `Income checks finished with issues.${tip}`.trim();
  if (cleaned.length > 160) return `${cleaned.slice(0, 157)}…`;
  return cleaned;
}

/** Deduplicate near-identical fail reason strings (case-insensitive). */
export function uniquePlainIncomeFailReasons(raw: unknown[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string' || !item.trim()) continue;
    const plain = plainIncomeFailReason(item.trim());
    const key = plain.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(plain);
  }
  return out;
}
export function summarizeLastActionResult(result: Record<string, unknown> | null | undefined): string | null {
  if (!result || typeof result !== 'object') return null;

  const msg =
    (typeof result.message === 'string' && result.message.trim()) ||
    (typeof result.error === 'string' && result.error.trim()) ||
    (typeof result.reason === 'string' && result.reason.trim()) ||
    null;
  if (msg) return msg;

  const failReasons = result.failReasons;
  if (Array.isArray(failReasons)) {
    const parts = failReasons
      .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
      .map((x) => x.trim());
    if (parts.length > 0) return parts.join(' · ');
  }

  const verdict = typeof result.verdict === 'string' ? result.verdict.trim().toUpperCase() : '';
  if (verdict === 'FAIL' || verdict === 'REJECTED' || verdict === 'DECLINED') {
    const income =
      typeof result.normalizedMonthlyIncome === 'number' && Number.isFinite(result.normalizedMonthlyIncome)
        ? ` ($${result.normalizedMonthlyIncome}/mo)`
        : '';
    return `Income verification ${verdict.toLowerCase()}${income}.`;
  }

  return null;
}

export function employmentActionOperatorNote(input: {
  lastAction?: string | null;
  lastActionOutcome?: string | null;
  completedMilestones?: string[];
  lastActionResult?: Record<string, unknown> | null;
}): string | null {
  return employmentLastActionOperatorCopy(input);
}

/** Application detail page — reviewer-facing load / error copy (no internal service names). */
export const APPLICATION_DETAIL_LOADING_TITLE = 'Loading application for review';

export const APPLICATION_DETAIL_LOADING_LEAD =
  'Gathering the latest information you need to review this case:';

export const APPLICATION_DETAIL_LOADING_ITEMS = [
  'Where the customer is in the journey and what happened so far',
  'Identity, income, and employment verification outcomes',
  'Decision engine and loan-review signals',
] as const;

export const APPLICATION_DETAIL_LOAD_ERROR_HINT =
  'If this keeps failing, wait a moment and retry. Contact platform ops if verification services may be unavailable.';
